/**
 * Gestión de clientes vinculados a una asesoría
 * Accesible desde el panel super_admin en /admin/empresa/:id/clientes
 */
import { useState, useEffect } from 'react'
import { FiLink, FiPlusCircle, FiTrash2, FiUsers, FiCheckCircle, FiSearch, FiCalendar, FiShield, FiX } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { asesoriaAPI, empresasAPI } from '../services/api'
import { formatearFecha } from '../utils/fechas'

// ── Helpers de presentación ──────────────────────────────────────────

const STATUS_CONFIG = {
  active:    { label: 'Activa',    bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  trialing:  { label: 'Trial',     bar: 'bg-blue-400',    badge: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' },
  past_due:  { label: 'Pago pend.',bar: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  canceled:  { label: 'Cancelada', bar: 'bg-red-400',     badge: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
  expired:   { label: 'Expirada',  bar: 'bg-red-500',     badge: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
}
const getStatus = (s) => STATUS_CONFIG[s] || { label: s || '—', bar: 'bg-gray-300', badge: 'bg-gray-50 text-gray-500 ring-1 ring-gray-200' }

const PLAN_BADGE = {
  avanzado: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  pro:      'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
}
const getPlanBadge = (p) => PLAN_BADGE[p] || 'bg-gray-50 text-gray-600 ring-1 ring-gray-200'

const Inicial = ({ nombre, size = 'md' }) => {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-11 h-11 text-base' }
  return (
    <div className={`${sizes[size]} rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm`}>
      {nombre?.charAt(0).toUpperCase() || '?'}
    </div>
  )
}

// ── Skeleton ─────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className="flex items-center gap-4 px-5 py-4 animate-pulse">
    <div className="w-1 self-stretch rounded-full bg-gray-100 flex-shrink-0" />
    <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 bg-gray-100 rounded-full w-40" />
      <div className="h-2.5 bg-gray-100 rounded-full w-24" />
    </div>
    <div className="h-5 w-14 bg-gray-100 rounded-full" />
    <div className="h-5 w-16 bg-gray-100 rounded-full" />
    <div className="h-3 w-20 bg-gray-100 rounded-full hidden md:block" />
    <div className="w-7 h-7 bg-gray-100 rounded-lg" />
  </div>
)

// ── Componente principal ──────────────────────────────────────────────

const GestionClientesAsesoria = () => {
  const { empresaId } = useEmpresaGestion()
  const [vinculos, setVinculos] = useState([])
  const [todasEmpresas, setTodasEmpresas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalVincular, setModalVincular] = useState(false)
  const [busquedaEmpresa, setBusquedaEmpresa] = useState('')
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [confirmDesvincular, setConfirmDesvincular] = useState(null) // vinculo a confirmar

  useEffect(() => { cargarDatos() }, [empresaId])

  const cargarDatos = async () => {
    setLoading(true)
    try {
      const [vinculosRes, empresasRes] = await Promise.all([
        asesoriaAPI.getAllVinculos(),
        empresasAPI.getAll()
      ])
      const misVinculos = (vinculosRes.data || []).filter(v => v.asesoria_id === empresaId)
      setVinculos(misVinculos)
      const vinculadasIds = new Set(misVinculos.map(v => v.cliente_id))
      const disponibles = (empresasRes.data?.empresas || [])
        .filter(e => e.tipo !== 'asesoria' && e.activo && !vinculadasIds.has(e.id))
      setTodasEmpresas(disponibles)
    } catch {
      toast.error('Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }

  const vincularEmpresa = async () => {
    if (!empresaSeleccionada) { toast.error('Selecciona una empresa'); return }
    setGuardando(true)
    try {
      await asesoriaAPI.vincular({ asesoria_id: empresaId, cliente_id: empresaSeleccionada.id, nivel_acceso: 'admin', notas: '' })
      toast.success(`${empresaSeleccionada.nombre} vinculada correctamente`)
      setModalVincular(false)
      setEmpresaSeleccionada(null)
      setBusquedaEmpresa('')
      cargarDatos()
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al vincular')
    } finally {
      setGuardando(false)
    }
  }

  const desvincularEmpresa = async (vinculo) => {
    try {
      await asesoriaAPI.deleteVinculo(vinculo.id)
      toast.success(`${vinculo.cliente_nombre} desvinculada`)
      setConfirmDesvincular(null)
      cargarDatos()
    } catch {
      toast.error('Error al desvincular')
    }
  }

  const empresasFiltradas = todasEmpresas.filter(e =>
    e.nombre?.toLowerCase().includes(busquedaEmpresa.toLowerCase()) ||
    e.email?.toLowerCase().includes(busquedaEmpresa.toLowerCase())
  )

  // ── Stats rápidas ─────────────────────────────────────────────────
  const activas  = vinculos.filter(v => v.cliente_status === 'active').length
  const trial    = vinculos.filter(v => v.cliente_status === 'trialing').length
  const problema = vinculos.filter(v => !['active','trialing'].includes(v.cliente_status)).length

  return (
    <div className="space-y-6">

      {/* ── Cabecera ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 tracking-tight">Cartera de clientes</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {vinculos.length} empresa{vinculos.length !== 1 ? 's' : ''} vinculada{vinculos.length !== 1 ? 's' : ''} a esta asesoría
          </p>
        </div>
        <button
          onClick={() => setModalVincular(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
        >
          <FiPlusCircle className="h-4 w-4" />
          Vincular empresa
        </button>
      </div>

      {/* ── KPIs ── */}
      {vinculos.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Activas',   value: activas,  color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { label: 'En trial',  value: trial,    color: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-100' },
            { label: 'Con alerta',value: problema, color: problema > 0 ? 'text-red-600' : 'text-gray-400', bg: problema > 0 ? 'bg-red-50' : 'bg-gray-50', border: problema > 0 ? 'border-red-100' : 'border-gray-100' },
          ].map(kpi => (
            <div key={kpi.label} className={`${kpi.bg} border ${kpi.border} rounded-xl px-4 py-3`}>
              <p className={`text-2xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</p>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">{kpi.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabla de clientes ── */}
      {loading ? (
        <div className="card-glass overflow-hidden divide-y divide-gray-50">
          {[1,2,3].map(i => <SkeletonRow key={i} />)}
        </div>
      ) : vinculos.length === 0 ? (
        <div className="card-glass flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mb-4">
            <FiLink className="h-8 w-8 text-teal-400" />
          </div>
          <p className="font-semibold text-gray-900 mb-1">Sin clientes vinculados</p>
          <p className="text-sm text-gray-400 max-w-xs">
            Vincula empresas para que esta asesoría pueda acceder a sus fichajes e informes
          </p>
          <button
            onClick={() => setModalVincular(true)}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <FiPlusCircle className="h-4 w-4" />
            Vincular primera empresa
          </button>
        </div>
      ) : (
        <div className="card-glass overflow-hidden">
          {/* Cabecera tabla */}
          <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-2.5 bg-gray-50/80 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Empresa</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-20 text-center">Plan</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-24 text-center">Estado</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-32 text-right">Vinculado</span>
            <span className="w-8" />
          </div>

          <div className="divide-y divide-gray-50">
            {vinculos.map((v) => {
              const status = getStatus(v.cliente_status)
              return (
                <div key={v.id} className="group flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">

                  {/* Franja de color — semáforo del estado de licencia */}
                  <div className={`w-1 self-stretch rounded-full ${status.bar} flex-shrink-0 opacity-70`} />

                  {/* Inicial */}
                  <Inicial nombre={v.cliente_nombre} />

                  {/* Nombre + meta */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate leading-snug">{v.cliente_nombre}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {v.vinculado_por_nombre && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <FiShield className="h-3 w-3" />
                          {v.vinculado_por_nombre}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Plan */}
                  <span className={`hidden md:inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold capitalize w-20 justify-center ${getPlanBadge(v.cliente_plan)}`}>
                    {v.cliente_plan || '—'}
                  </span>

                  {/* Estado */}
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold w-24 justify-center ${status.badge}`}>
                    {status.label}
                  </span>

                  {/* Fecha */}
                  <span className="hidden md:flex items-center gap-1.5 text-xs text-gray-400 w-32 justify-end flex-shrink-0">
                    <FiCalendar className="h-3 w-3" />
                    {formatearFecha(v.created_at)}
                  </span>

                  {/* Acción */}
                  <button
                    onClick={() => setConfirmDesvincular(v)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                    title="Desvincular"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Modal: Vincular empresa ── */}
      {modalVincular && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

            {/* Header modal */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
                  <FiLink className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-none">Vincular empresa</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{todasEmpresas.length} disponible{todasEmpresas.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => { setModalVincular(false); setEmpresaSeleccionada(null); setBusquedaEmpresa('') }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {/* Buscador */}
            <div className="px-6 pt-4 pb-3">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={busquedaEmpresa}
                  onChange={e => setBusquedaEmpresa(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista de empresas */}
            <div className="px-3 pb-2 max-h-64 overflow-y-auto">
              {empresasFiltradas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <FiUsers className="h-8 w-8 text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">
                    {busquedaEmpresa ? `Sin resultados para "${busquedaEmpresa}"` : 'Todas las empresas ya están vinculadas'}
                  </p>
                </div>
              ) : empresasFiltradas.map(emp => {
                const selected = empresaSeleccionada?.id === emp.id
                return (
                  <button
                    key={emp.id}
                    onClick={() => setEmpresaSeleccionada(emp)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all mb-0.5 ${
                      selected
                        ? 'bg-teal-50 ring-1 ring-teal-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all ${
                      selected
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {emp.nombre?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${selected ? 'text-teal-800' : 'text-gray-900'}`}>
                        {emp.nombre}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{emp.email || `Plan ${emp.plan}`}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      selected ? 'border-teal-500 bg-teal-500' : 'border-gray-200'
                    }`}>
                      {selected && <FiCheckCircle className="h-3 w-3 text-white" />}
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Footer modal */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => { setModalVincular(false); setEmpresaSeleccionada(null); setBusquedaEmpresa('') }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                disabled={guardando}
              >
                Cancelar
              </button>
              <button
                onClick={vincularEmpresa}
                disabled={!empresaSeleccionada || guardando}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {guardando ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Vinculando...
                  </>
                ) : (
                  <>
                    <FiLink className="h-4 w-4" />
                    {empresaSeleccionada ? `Vincular ${empresaSeleccionada.nombre.split(' ')[0]}` : 'Selecciona una empresa'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmar desvincular ── */}
      {confirmDesvincular && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FiTrash2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">¿Desvincular empresa?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  <strong>{confirmDesvincular.cliente_nombre}</strong> perderá acceso a esta asesoría. Se puede volver a vincular en cualquier momento.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDesvincular(null)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => desvincularEmpresa(confirmDesvincular)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors"
              >
                Sí, desvincular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionClientesAsesoria
